import React, { Component } from 'react';
import { hotjar } from 'react-hotjar';
import { Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';

import { withStyles } from '~/assets/withStyles';
import { localeDetails, updateOrganizationLocaleDetails } from '~/components/CmsMain/localeGlobals';
import withMinimizedDialog from '~/components/core/MinimizedBar/Context';
import { MinimizedBarContextProvider } from '~/components/core/MinimizedBar/Context/ToastifyBarContext';
import ALL_DEPLOY_ENV from '~/server_shared/generated-types/ALL_DEPLOY_ENV';
import { isUserImpersonated } from '~/UserUtils';
import {
  CACHE_TTL,
  checkFrontendVersionAndReload,
  compose,
  isProductionOrUat,
  logoutCurrentUser,
  trackLandingPage,
  trackPage,
  wrapAsyncApiCallWithCache,
} from '~/Utils';
import { getRegionCode } from '~/Utils/regionUtils';

import rootStyles from '../../assets/rootStyles';
import { MissingProfilePage } from '../core/PermissionError/PermissionDeniedPage';
import { isPermissionsEnabled } from '../core/Permissions/PermissionUtils';
import { CurrencyFormatterContextProvider } from '../CurrencyFormatterContext';
import { installGuardrailsInterceptor } from '../Guardrails/guardrailsInterceptor';
import GuardrailsViolationDialogHost from '../Guardrails/GuardrailsViolationDialogHost';
import { CmsContext, withCmsContext } from '../hooks/useCms';
import ZenDesk from '../hooks/useZenDesk';
import LoadingIndicator from '../LoadingIndicator';
import LoginContainer, { LoginSsoPage } from '../Login';
import { OrganizationContextProvider } from '../OrganizationContext';
import SessionTimeoutWarning from '../SessionTimeoutWarning/SessionTimeoutWarning';
import { installSorInterceptor } from '../SystemOfRecord/sorInterceptor';
import TopLevelDialog from '../TopLevelDialog/TopLevelDialog';

import CmsMainAuthenticatedInner from './CmsMainAuthenticatedInner';
import {
  globalDeployEnv,
  isSessionTimeoutWarningActive,
  setGlobalDeployEnv,
  setGlobalImageProxyBaseUrl,
  setGlobalIsCloudFeatureBranch,
  setSessionTimeoutWarningActive,
  setTopLevelDialogSetFunc,
} from './globals';
import LoginRedirect from './LoginRedirect';
import mixpanel from './mixpanel';
import { consumePostLoginReturnPath } from './postLoginReturnPath';
import sentry from './sentry';

axios.defaults.headers.common['X-FiveSigma-Instance'] = uuidv4();

const CmsMainAuthenticated = compose(
  (C) => withStyles(C, rootStyles),
  withCmsContext,
  withRouter,
  withMinimizedDialog
)(CmsMainAuthenticatedInner);

class CmsMainInner extends Component {
  state = {
    user: null,
    lastLoginTimeCheck: null,
    isLoggedIn: false,
    isLoading: true,
    isError: false,
    isLoadingTwilioDetails: true,
    pathToRedirectOnSuccess: '/',
    topLevelDialogOpen: false,
    topLevelDialogTitle: '',
    topLevelDialogMessage: '',
    topLevelDialogContentComponent: null,
    showTopLevelDialogOkButton: false,
    isSessionTimeoutDialog: false,
    sessionExpiresAt: null,
    callInProgress: this.props.location.pathname.startsWith('/call_in_progress')
      ? { direction: 'Incoming' }
      : undefined,
    pageTitle: '',
    showLoading: false,
  };

  unListenHistory = null;

  guardrailsInterceptorId = null;

  sorInterceptorIds = null;

  retryInterceptorIds = null;

  componentDidMount() {
    const { history } = this.props;
    const { callInProgress } = this.state;
    this.retryInterceptorIds = axiosRetry(axios, { retries: 3, retryDelay: exponentialDelay });
    // Before the guardrails one: a guardrails dialog's wait on the user is not a wait on the System of Record.
    this.sorInterceptorIds = installSorInterceptor(axios);
    this.guardrailsInterceptorId = installGuardrailsInterceptor(axios);
    this.initSession();
    this.initTwilioDetails();
    trackLandingPage(history.location);

    this.unListenHistory = history.listen((location) => {
      if (!callInProgress) {
        checkFrontendVersionAndReload();
      }

      // TODO: Generalize all trackers into "TrackingService" once POC is over
      try {
        mixpanel?.trackPageView();
      } catch (error) {
        if (!isProductionOrUat()) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }

      trackPage(location);
    });

    setTopLevelDialogSetFunc(this.setTopLevelDialog);
  }

  componentDidUpdate() {
    const { isLoggedIn, lastLoginTimeCheck, isError } = this.state;
    const TIME_BETWEEN_LOGIN_CHECKS_MSEC = 60 * 1000;
    const now = Date.now();
    // every TIME_BETWEEN_LOGIN_CHECKS verify user is still logged in
    // Skip polling while the session timeout warning is active: it owns the session's end and its refresh
    if (
      isLoggedIn &&
      lastLoginTimeCheck &&
      now - lastLoginTimeCheck > TIME_BETWEEN_LOGIN_CHECKS_MSEC &&
      !isSessionTimeoutWarningActive
    ) {
      if (isError !== true) {
        this.initSession();
        this.initTwilioDetails();
        this.setState({ lastLoginTimeCheck: now });
      } else {
        window.location.reload();
      }
    }
  }

  componentWillUnmount() {
    if (this.unListenHistory) {
      this.unListenHistory();
    }
    // Eject what this mount installed on the shared axios instance, so a remount
    // (StrictMode dev, or a full auth teardown/re-login) doesn't stack a second copy:
    // duplicate guardrails dialogs/replays, and a retry pair that never goes away.
    if (this.guardrailsInterceptorId !== null) {
      axios.interceptors.response.eject(this.guardrailsInterceptorId);
      this.guardrailsInterceptorId = null;
    }
    if (this.sorInterceptorIds !== null) {
      axios.interceptors.request.eject(this.sorInterceptorIds.requestInterceptorId);
      axios.interceptors.response.eject(this.sorInterceptorIds.responseInterceptorId);
      this.sorInterceptorIds = null;
    }
    if (this.retryInterceptorIds !== null) {
      axios.interceptors.request.eject(this.retryInterceptorIds.requestInterceptorId);
      axios.interceptors.response.eject(this.retryInterceptorIds.responseInterceptorId);
      this.retryInterceptorIds = null;
    }
  }

  setTopLevelDialog = ({ title, message, contentComponent, showOkButton, isSessionTimeout = false }) => {
    this.setState({
      topLevelDialogOpen: true,
      topLevelDialogTitle: title,
      topLevelDialogMessage: message,
      topLevelDialogContentComponent: contentComponent,
      showTopLevelDialogOkButton: showOkButton,
      isSessionTimeoutDialog: isSessionTimeout,
    });
  };

  closeTopLevelDialog = () => {
    const { isSessionTimeoutDialog } = this.state;
    let newState = {
      topLevelDialogOpen: false,
      topLevelDialogTitle: '',
      topLevelDialogMessage: '',
      topLevelDialogContentComponent: null,
      showTopLevelDialogOkButton: false,
      isSessionTimeoutDialog: false,
    };

    // If this is a session timeout dialog, set logged out state which will naturally redirect to login
    if (isSessionTimeoutDialog) {
      newState = {
        ...newState,
        isLoggedIn: false,
        isLoading: false,
        showLoading: false,
        isMissingProfile: false, // Reset to ensure redirect to login, not MissingProfilePage
      };
    }

    this.setState(newState);
  };

  initSession = (pathToRedirectOnSuccess = '/') => {
    // Set timeout to delay the loading component
    const timer = setTimeout(() => {
      if (this.state.isLoading) {
        this.setState({ showLoading: true });
      }
    }, 1000);

    // Copies cached under the pre-v2 key hold the organization's whole configuration, credentials included.
    localforage.removeItem('user_organization');

    return Promise.allSettled([
      axios.get('/api/v1/users/session'),
      wrapAsyncApiCallWithCache({
        fn: () => axios.get('/api/v1/users/user_organization_details'),
        cacheKey: 'user_organization_v2',
        cacheTtl: CACHE_TTL,
        shouldCacheResultFunc: (data) => data?.is_logged_in,
      }),
    ]).then(
      ([
        { status: sessionStatus, value: response, reason: sessionReason },
        { status: userOrganizationStatus, value: userOrganizationResponse, reason: userOrganizationReason },
      ]) => {
        // Cleanup timeout if the promise is resolved before the timeout
        clearTimeout(timer);
        if (sessionStatus === 'rejected' || userOrganizationStatus === 'rejected') {
          if (sessionReason.response && sessionReason.response.status === 401) {
            // Check if this is a session timeout while user was active - show dialog first, redirect after user closes it
            // Only show dialog if user was previously logged in (dialog renders inside isLoggedIn block)
            if (sessionReason.response.data?.session_timeout_detected && this.state.isLoggedIn) {
              // SessionTimeoutWarning shows only when within 2 min of expiry. Fallback when it never showed
              // (e.g. tab in background, server enforced before client warning, session invalidated elsewhere).
              if (!isSessionTimeoutWarningActive) {
                this.setTopLevelDialog({
                  title: 'Session Expired',
                  message: 'Your session has expired due to inactivity. Please log in again.',
                  showOkButton: true,
                  isSessionTimeout: true,
                });
              }
              return;
            }
            this.setState({ isLoggedIn: false, isLoading: false, showLoading: false });
            return;
          }

          if (
            userOrganizationReason?.errors &&
            userOrganizationReason.errors.some((error) => error?.response?.status === 401)
          ) {
            this.setState({ isLoggedIn: false, isLoading: false, showLoading: false });
            return;
          }

          this.setState({ isError: true, showLoading: false });
          return;
        }

        if (response.data.is_logged_in) {
          const updatingMethod = ['post', 'put', 'patch', 'delete'];
          for (const methodIdx in updatingMethod) {
            axios.defaults.headers[updatingMethod[methodIdx]]['X-CSRFToken'] = response.data.csrf_token;
          }
        }

        const resDeployEnv = response.data.deploy_env.toUpperCase();

        if (!ALL_DEPLOY_ENV.includes(resDeployEnv)) {
          throw Error(`Invalid deploy environment: ${resDeployEnv}`);
        }

        setGlobalDeployEnv(resDeployEnv);
        setGlobalIsCloudFeatureBranch(Boolean(response.data.is_cloud_feature_branch));
        // Per-env image proxy origin — SecureIframe reads this to build its
        // CSP img-src directive without hardcoding a URL.
        setGlobalImageProxyBaseUrl(response.data.image_proxy_base_url || '');

        sentry?.initialize(resDeployEnv);

        try {
          mixpanel?.initialize(resDeployEnv);
          mixpanel?.setBaseProperties({
            environment: globalDeployEnv,
            db_region: getRegionCode(),
          });
        } catch (error) {
          if (!isProductionOrUat()) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }

        if (resDeployEnv === 'UAT') {
          hotjar.initialize(1659665, 6);
        } else if (resDeployEnv === 'PROD') {
          hotjar.initialize(1659668, 6);
        }

        if (!response.data.is_logged_in || !userOrganizationResponse.is_logged_in || !response.data.user) {
          // Check if session timed out while user was active - show dialog first, redirect after user closes it
          // Only show dialog if user was previously logged in (dialog renders inside isLoggedIn block)
          if (response.data.session_timeout_detected && this.state.isLoggedIn) {
            // SessionTimeoutWarning shows only when within 2 min of expiry. Fallback when it never showed
            // (e.g. tab in background, server enforced before client warning, session invalidated elsewhere).
            if (!isSessionTimeoutWarningActive) {
              this.setTopLevelDialog({
                title: 'Session Expired',
                message: 'Your session has expired due to inactivity. Please log in again.',
                showOkButton: true,
                isSessionTimeout: true,
              });
            }
            return;
          }
          this.setState({ isLoggedIn: false, isLoading: false, showLoading: false });
          return;
        }

        const user = response.data.user;

        const userOrganization = userOrganizationResponse.user_organization;
        updateOrganizationLocaleDetails(userOrganization, user);

        sentry?.setUser(user);

        // An SSO sign-in comes back to '/', without the state.from the login redirect carried.
        const postLoginReturnPath = consumePostLoginReturnPath(pathToRedirectOnSuccess, this.props.location.pathname);

        this.setState({
          user,
          userOrganization,
          userProfile: response.data.user_profile,
          canUpdatePassword: Boolean(response.data.can_update_password),
          isLoggedIn: true,
          isLoading: false,
          showLoading: false,
          lastLoginTimeCheck: Date.now(),
          pathToRedirectOnSuccess,
          isMissingProfile: isPermissionsEnabled(userOrganization) && !response.data.user_profile,
          sessionExpiresAt: response.data.session_expires_at || null,
        });

        if (postLoginReturnPath) {
          this.props.history.replace(postLoginReturnPath);
        }

        try {
          mixpanel?.identifyUser(user.id);
          mixpanel?.setBaseProperties({
            $name: user?.username,
            user_role_type: user?.role?.role_type,
            impersonated: isUserImpersonated(user),
            organization: userOrganization?.name,
            organization_id: userOrganization?.id,
            environment: globalDeployEnv,
            db_region: getRegionCode(),
          });
        } catch (error) {
          if (!isProductionOrUat()) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
      }
    );
  };

  initTwilioDetails = async () => {
    try {
      const { data: twilioDetails } = await axios.get('/api/v1/users/user_twilio_details');
      this.setState({
        isLoadingTwilioDetails: false,
        twilioWorkspaceDetails: twilioDetails,
      });
    } catch {
      this.setState({
        isLoadingTwilioDetails: false,
        twilioWorkspaceDetails: null,
      });
    }
  };

  handleSessionRefreshed = (newExpiresAt) => {
    setSessionTimeoutWarningActive(false);
    this.setState({ sessionExpiresAt: newExpiresAt, lastLoginTimeCheck: Date.now() });
  };

  handleSessionExpired = () => {
    setSessionTimeoutWarningActive(true);
  };

  handleWarningStateChange = (isActive) => {
    setSessionTimeoutWarningActive(isActive);
  };

  setCallInProgress = (newState) => {
    this.setState({ callInProgress: newState });
  };

  setPageTitle = (newPageTitle, documentTitle = undefined) => {
    this.setState({ pageTitle: newPageTitle });
    document.title = documentTitle || newPageTitle;
  };

  render() {
    // eslint-disable-next-line react/prop-types
    const { location } = this.props;
    const {
      user,
      userOrganization,
      userProfile,
      canUpdatePassword,
      isLoggedIn,
      isLoading,
      isError,
      pathToRedirectOnSuccess,
      topLevelDialogOpen,
      topLevelDialogTitle,
      topLevelDialogMessage,
      topLevelDialogContentComponent,
      showTopLevelDialogOkButton,
      callInProgress,
      twilioWorkspaceDetails,
      pageTitle,
      isMissingProfile,
      showLoading,
      sessionExpiresAt,
    } = this.state;

    if (!user && isLoading) {
      return showLoading ? <LoadingIndicator isError={isError} /> : null;
    }

    if (isMissingProfile) {
      return <MissingProfilePage />;
    }

    if (isLoggedIn) {
      // on successful login (or if someone manually tried reaching here), redirect to original path
      if (location.pathname.startsWith('/login')) {
        return <Redirect to={pathToRedirectOnSuccess} />;
      }

      return (
        <CmsContext.Provider
          value={{
            user,
            userOrganization,
            userProfile,
            canUpdatePassword,
            userReLogin: this.initSession,
            userLogout: logoutCurrentUser,
            callInProgress,
            setCallInProgress: this.setCallInProgress,
            userReloadTwilioDetails: this.initTwilioDetails,
            twilioWorkspaceDetails,
            pageTitle,
            setPageTitle: this.setPageTitle,
          }}
        >
          <OrganizationContextProvider>
            <CurrencyFormatterContextProvider currency={localeDetails.currency}>
              <MinimizedBarContextProvider>
                <CmsMainAuthenticated />
                <GuardrailsViolationDialogHost />
                <TopLevelDialog
                  isOpen={topLevelDialogOpen}
                  title={topLevelDialogTitle}
                  message={topLevelDialogMessage}
                  contentComponent={topLevelDialogContentComponent}
                  showOkButton={showTopLevelDialogOkButton}
                  onClose={this.closeTopLevelDialog}
                />
                <SessionTimeoutWarning
                  sessionExpiresAt={sessionExpiresAt}
                  isCallInProgress={!!callInProgress}
                  onSessionRefreshed={this.handleSessionRefreshed}
                  onSessionExpired={this.handleSessionExpired}
                  onWarningStateChange={this.handleWarningStateChange}
                />
              </MinimizedBarContextProvider>
            </CurrencyFormatterContextProvider>
          </OrganizationContextProvider>
          <ZenDesk />
        </CmsContext.Provider>
      );
    }

    if (!location.pathname.startsWith('/login')) {
      return <LoginRedirect pathname={location.pathname} search={location.search} />;
    }

    if (location.pathname === '/login_sso') {
      return <LoginSsoPage />;
    } else {
      return (
        <LoginContainer
          onLoginSuccess={() => this.initSession(location.state && location.state.from ? location.state.from : '/')}
        />
      );
    }
  }
}

CmsMainInner.propTypes = {
  history: PropTypes.object.isRequired, // comes from withRouter
  location: PropTypes.object.isRequired, // comes from withRouter
};

const CmsMain = withRouter(CmsMainInner);

export { withCmsContext };
export default CmsMain;
